import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UsageLimits, UsageStatus } from '@/types/usage'
import { usageService } from '@/services/api/usage'

export const useUsageStore = defineStore('usage', () => {
    const limits = ref<UsageLimits | null>(null)
    const isLoading = ref(false)
    const lastFetch = ref<Date | null>(null)

    // Computed: Status de uso
    const usageStatus = computed<UsageStatus | null>(() => {
        if (!limits.value) return null

        const totalLeadsRemaining = limits.value.total_leads.unlimited
            ? Infinity
            : limits.value.total_leads.limit - limits.value.total_leads.current

        const searchLeadsRemaining = limits.value.lead_search.unlimited
            ? Infinity
            : limits.value.lead_search.limit - limits.value.lead_search.current

        // Calcula dias até o reset (dia 15)
        const now = new Date()
        const currentMonth = now.getMonth()
        const currentYear = now.getFullYear()
        const resetDay = 15

        let nextResetDate = new Date(currentYear, currentMonth, resetDay)
        if (now.getDate() >= resetDay) {
            nextResetDate = new Date(currentYear, currentMonth + 1, resetDay)
        }

        const daysUntilReset = Math.ceil(
            (nextResetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        )

        return {
            can_add_leads: totalLeadsRemaining > 0,
            can_search_leads: searchLeadsRemaining > 0,
            total_leads_remaining: totalLeadsRemaining,
            search_leads_remaining: searchLeadsRemaining,
            days_until_reset: daysUntilReset
        }
    })

    // Computed: Percentuais de uso
    const totalLeadsPercentage = computed(() => {
        if (!limits.value || limits.value.total_leads.unlimited) return 0
        return Math.min(
            (limits.value.total_leads.current / limits.value.total_leads.limit) * 100,
            100
        )
    })

    const searchLeadsPercentage = computed(() => {
        if (!limits.value || limits.value.lead_search.unlimited) return 0
        return Math.min(
            (limits.value.lead_search.current / limits.value.lead_search.limit) * 100,
            100
        )
    })

    // Buscar limites reais do backend
    async function fetchLimits(): Promise<void> {
        try {
            isLoading.value = true
            limits.value = await usageService.getLimits()
            lastFetch.value = new Date()
        } catch (error) {
            console.error('Error fetching usage limits:', error)
            throw error
        } finally {
            isLoading.value = false
        }
    }

    // Atualizar contadores localmente (otimista)
    function incrementTotalLeads(count: number = 1): void {
        if (limits.value && !limits.value.total_leads.unlimited) {
            limits.value.total_leads.current += count
        }
    }

    function decrementTotalLeads(count: number = 1): void {
        if (limits.value && !limits.value.total_leads.unlimited) {
            limits.value.total_leads.current = Math.max(
                0,
                limits.value.total_leads.current - count
            )
        }
    }

    function incrementSearchLeads(count: number = 1): void {
        // Atualização otimista local
        if (limits.value && !limits.value.lead_search.unlimited) {
            limits.value.lead_search.current += count
        }
        // Sincroniza com backend e re-busca para garantir consistência
        usageService.incrementSearchCount(count)
            .then(() => fetchLimits())
            .catch(err => console.warn('Failed to sync search count:', err))
    }

    // Verificar se pode executar ação
    function canAddLeads(quantity: number = 1): boolean {
        if (!limits.value) return false
        if (limits.value.total_leads.unlimited) return true

        const remaining = limits.value.total_leads.limit - limits.value.total_leads.current
        return remaining >= quantity
    }

    function canSearchLeads(quantity: number = 1): boolean {
        if (!limits.value) return false
        if (limits.value.lead_search.unlimited) return true

        const remaining = limits.value.lead_search.limit - limits.value.lead_search.current
        return remaining >= quantity
    }

    // Reset (para quando mudar o mês ou logout)
    function reset(): void {
        limits.value = null
        lastFetch.value = null
    }

    return {
        // State
        limits,
        isLoading,
        lastFetch,

        // Computed
        usageStatus,
        totalLeadsPercentage,
        searchLeadsPercentage,

        // Actions
        fetchLimits,
        incrementTotalLeads,
        decrementTotalLeads,
        incrementSearchLeads,
        canAddLeads,
        canSearchLeads,
        reset
    }
})
